/**
 * NewBusinessProposalView — full prospect-facing presentation deck for
 * net-new agencies. Built dynamically from the products & bundles in the
 * proposal:
 *
 *   - Hero with agency name + overview video
 *   - Alternating service sections (one per product category the prospect picked)
 *   - Custom bundles showcase
 *   - Pricing table at the bottom with vendor-price reveal toggle + margin
 *
 * Used by ProposalPublicView when proposal_type === "new_business".
 */
import { useMemo, useState, useEffect, useRef } from "react";
import {
  Camera,
  Zap,
  Layout,
  Video,
  Plane,
  Building2,
  Sparkles,
  Mic,
  CheckCircle2,
  Eye,
  EyeOff,
  TrendingUp,
  Package,
  CreditCard,
  CalendarClock,
  Handshake,
  X,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import ProposalRoiCalculator from "./ProposalRoiCalculator";
import { buildLayoutBlocks, DEFAULT_SMALL_PRINT } from "./proposalLayout";
import { roiVisible } from "./roiMath";
import ProposalPriceList, { boltOnPrice } from "./ProposalPriceList";
import OverviewAudio from "./OverviewAudio";
import ProductShowcase from "./ProductShowcase";
import { applyCustomExamples, EMPTY_VIDEOS } from "./exampleVideos";
import "./ShowcaseLanding.css";

const API = process.env.REACT_APP_BACKEND_URL;
import "./NewBusinessProposalView.css";

const ICONS = { Camera, Zap, Layout, Video, Plane, Building2, Sparkles, Mic };
const FALLBACK_ICON = Package;

// Per-category image sets that fade through in a slideshow alongside the copy.
// Add more arrays here later to power other service sections.
const CATEGORY_MEDIA = {
  Photography: [
    "https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/gmy3joz5_NicheCom-property-marketing-header-banner-Bath-2100x1182.png",
    "https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/4q6wqc3u_lower-res-twilight-property-photograph-of-architect-designed-home-webp-2100x1182.png",
    "https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/wlwel2te_drone3-2100x1182.png",
    "https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/rfp03ij3_banner-temp-1.png",
  ],
  Floorplans: [
    "https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/06uy2hcp_New_Floorplan_NicheCom.png",
  ],
};

// Per-category video clips — landscape clip fills the frame; optional vertical clip
// is shown picture-in-picture inside a phone mockup overlay on top of the landscape.
const CATEGORY_VIDEO = {
  Video: {
    landscape:
      "https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/tf7ayet1_NicheCom_Prop_Vids_short_sml.mp4",
    // When a true portrait clip is available, swap this URL. Falls back to the
    // landscape clip object-fit:cover'd to portrait so the overlay still works
    // before a dedicated vertical export is delivered.
    portrait:
      "https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/tf7ayet1_NicheCom_Prop_Vids_short_sml.mp4",
  },
};

// Lightweight fading slideshow — preloads images, fades each one over 5s
function FadeSlideshow({ images, alt = "" }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!images || images.length < 2) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 5000);
    return () => clearInterval(t);
  }, [images]);
  if (!images || images.length === 0) return null;
  return (
    <div className="nbp-slideshow" data-testid="nbp-slideshow">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          loading={i === 0 ? "eager" : "lazy"}
          className={`nbp-slide ${i === idx ? "nbp-slide-active" : ""}`}
        />
      ))}
    </div>
  );
}

/**
 * FloorplanBackdrop — Decorative SVG that sits behind the Floorplans
 * service section. Strokes are dashed and animate via stroke-dashoffset
 * to look like an architect's plan being drawn out.
 *
 * Pure SVG + CSS (no JS); each <path> has a unique --delay so the lines
 * appear staggered, then fade gently.
 */
function FloorplanBackdrop() {
  return (
    <svg
      className="nbp-floorplan-bg"
      viewBox="0 0 1200 700"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      <g className="nbp-fp-grid">
        {/* Outer house outline — main rectangle with a small bay */}
        <path
          d="M 140 130 L 720 130 L 720 230 L 840 230 L 840 460 L 720 460 L 720 560 L 140 560 Z"
          style={{ "--delay": "0s", "--dur": "14s" }}
        />
        {/* Top horizontal divider */}
        <path d="M 140 280 L 720 280" style={{ "--delay": "1.6s", "--dur": "8s" }} />
        {/* Middle horizontal divider */}
        <path d="M 140 410 L 840 410" style={{ "--delay": "3.2s", "--dur": "9s" }} />
        {/* Bedroom 1 wall */}
        <path d="M 360 130 L 360 280" style={{ "--delay": "3.8s", "--dur": "6.5s" }} />
        {/* Bedroom 2 wall */}
        <path d="M 540 130 L 540 280" style={{ "--delay": "4.8s", "--dur": "6.5s" }} />
        {/* Hallway vertical */}
        <path d="M 460 280 L 460 410" style={{ "--delay": "6s", "--dur": "6.5s" }} />
        {/* Lounge / kitchen split */}
        <path d="M 580 410 L 580 560" style={{ "--delay": "7.2s", "--dur": "8s" }} />
        {/* Bay window protrusion */}
        <path d="M 720 230 L 840 230 L 840 460 L 720 460" style={{ "--delay": "8.4s", "--dur": "8s" }} />
        {/* Door swing arcs */}
        <path
          d="M 360 280 A 36 36 0 0 1 396 244"
          style={{ "--delay": "9.4s", "--dur": "5s" }}
          fill="none"
        />
        <path
          d="M 540 280 A 36 36 0 0 0 504 244"
          style={{ "--delay": "10.2s", "--dur": "5s" }}
          fill="none"
        />
        <path
          d="M 460 410 A 36 36 0 0 1 496 446"
          style={{ "--delay": "11s", "--dur": "5s" }}
          fill="none"
        />
        {/* Dimension tick marks */}
        <path d="M 140 100 L 720 100" style={{ "--delay": "11.6s", "--dur": "6.5s" }} />
        <path d="M 140 90 L 140 110" style={{ "--delay": "12.2s", "--dur": "1.6s" }} />
        <path d="M 720 90 L 720 110" style={{ "--delay": "12.4s", "--dur": "1.6s" }} />
        <path d="M 90 130 L 90 560" style={{ "--delay": "12.6s", "--dur": "6.5s" }} />
        <path d="M 80 130 L 100 130" style={{ "--delay": "13.2s", "--dur": "1.6s" }} />
        <path d="M 80 560 L 100 560" style={{ "--delay": "13.4s", "--dur": "1.6s" }} />
      </g>
    </svg>
  );
}

/**
 * EPCChart — Animated SVG energy-performance chart. The green house outline
 * draws itself first, then each coloured band (A→G) slides out from left to
 * right, staggered, like an EPC certificate being plotted live.
 *
 * Animation only fires once the chart scrolls into view (IntersectionObserver),
 * not on page load.
 */
function EPCChart() {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Bar geometry — each "arrow band" has its own length, colour and stagger
  const BARS = [
    { letter: "A", y: 80,  width: 220, fill: "#0d8a3f", delay: 0.5 },
    { letter: "B", y: 175, width: 280, fill: "#3aaa35", delay: 0.85 },
    { letter: "C", y: 270, width: 360, fill: "#7ac143", delay: 1.2 },
    { letter: "D", y: 365, width: 440, fill: "#ffed00", text: "#3a3300", delay: 1.55 },
    { letter: "E", y: 460, width: 510, fill: "#f6b545", delay: 1.9 },
    { letter: "F", y: 555, width: 580, fill: "#ef7d1a", delay: 2.25 },
    { letter: "G", y: 650, width: 650, fill: "#e30613", delay: 2.6 },
  ];

  return (
    <div ref={ref} className={`nbp-epc ${active ? "nbp-epc-active" : ""}`}>
      <svg
        className="nbp-epc-svg"
        viewBox="0 0 1080 800"
        aria-hidden="true"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Green house outline — strokes itself in like the floorplan */}
        <g className="nbp-epc-house">
          <path
            d="M 60 740 L 60 240 L 215 80 L 215 30 L 270 30 L 270 130 L 370 240 L 370 740 Z"
          />
          <path d="M 215 240 L 370 240" />
        </g>
        <text className="nbp-epc-label" x="120" y="600">Energy</text>
        <text className="nbp-epc-label" x="100" y="660">Efficiency</text>

        {/* Coloured arrow bars — each slides out from the left */}
        {BARS.map((b) => (
          <g
            key={b.letter}
            className="nbp-epc-bar"
            style={{ "--delay": `${b.delay}s` }}
          >
            <path
              fill={b.fill}
              d={`M 400 ${b.y} L ${400 + b.width - 50} ${b.y} L ${400 + b.width} ${b.y + 42.5} L ${400 + b.width - 50} ${b.y + 85} L 400 ${b.y + 85} Z`}
            />
            <text
              x={400 + b.width - 70}
              y={b.y + 42.5}
              dy="0.36em"
              className="nbp-epc-letter"
              fill={b.text || "#ffffff"}
            >
              {b.letter}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}



// Default copy for each category. The RD doesn't write per-product copy in
// the builder — the page generates rich descriptions from the products the
// agency picked. This keeps the deck consistent across proposals.
const CATEGORY_COPY = {
  Photography: {
    title: "Professional Photography",
    tagline: "First impressions win listings.",
    body:
      "Every property gets the full studio treatment — bright, wide, considered shots that pull buyers in on the portals and stop the scroll on social. Edited overnight, branded and ready by morning.",
  },
  Floorplans: {
    title: "Floorplans",
    tagline: "The bit buyers actually study.",
    body:
      "Clean 2D floorplans with proper measurements, branded to your agency and ready to drop straight into your listing. Floorplan + EPC packages save you a callout and a few quid.",
  },
  EPCs: {
    title: "Energy Performance Certificates",
    tagline: "Compliant, fast, no fuss.",
    body:
      "Accredited assessors come out alongside your photography or floorplan visit. EPCs back in 24-48 hours, lodged correctly, never a question mark on the listing.",
  },
  Video: {
    title: "Property Video",
    tagline: "Let the home tell its own story.",
    body:
      "From portrait property reels to presenter-led flagship tours with drone — we shoot, edit and brand video that performs on the portals, on social, and in your seller pitches.",
  },
  "3D Showcase": {
    title: "3D Showcases",
    tagline: "An open house that never closes.",
    body:
      "Matterport-quality walkthroughs that let buyers self-tour the property at any time, day or night. Drives qualified enquiries and reduces wasted viewings.",
  },
  Social: {
    title: "Social, Sorted",
    tagline: "Done-for-you social on every listing.",
    body:
      "Sneak peeks, property reels, portrait social images and presenter-led tours — produced from the same on-site shoot and delivered ready to publish. Get listings working harder on every channel.",
  },
};

const fmtPrice = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return n;
  return `£${num.toLocaleString("en-GB")}`;
};

// "20 photos" / "up to 10 photos" for photography products (count is per-proposal editable)
const photoLabel = (sp) => {
  const n = sp.photo_count ?? sp.product?.photo_count;
  if (n == null || n === "") return null;
  const prefix = sp.product?.photo_count_prefix ? `${sp.product.photo_count_prefix} ` : "";
  return `${prefix}${n} ${Number(n) === 1 ? "photo" : "photos"}`;
};

export default function NewBusinessProposalView({ proposal, catalogue }) {
  const [revealVendor, setRevealVendor] = useState(false);
  // Global "Add EPC" toggle — applies a £40 uplift across all package cards
  const [epcEnabled, setEpcEnabled] = useState(false);
  // Get Started flow: which CTA the prospect picked, plus the modal state
  const [activeCta, setActiveCta] = useState(null);  // null | 'accept_proposal' | 'pay_card' | 'direct_debit'

  // Resolve selected products against the catalogue
  const productById = useMemo(() => {
    const m = {};
    (catalogue?.products || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [catalogue]);

  const selectedFull = useMemo(() => {
    return (proposal.selected_products || [])
      .map((sp) => ({ ...sp, product: productById[sp.product_id] }))
      .filter((sp) => sp.product);
  }, [proposal.selected_products, productById]);

  // Blocks in the RD's saved order (layout_order): category sections at first appearance,
  // the packages block where the first bundle sits. Legacy proposals (no layout_order)
  // fall back to Photography → Floorplans → Video → 3D → Social → others → EPCs, then packages.
  const { blocks, orderedProducts, orderedBundles } = useMemo(
    () => buildLayoutBlocks(selectedFull, proposal.bundles || [], proposal.layout_order),
    [selectedFull, proposal.bundles, proposal.layout_order]
  );
  const sections = useMemo(() => blocks.filter((b) => b.type === "section"), [blocks]);
  const smallPrint = (proposal.small_print ?? DEFAULT_SMALL_PRINT).trim();

  // Social section carries the Social Go / Social Pro showcase (with this proposal's custom examples)
  const hasSocial = sections.some((s) => s.category === "Social");
  const [videos, setVideos] = useState(null);
  useEffect(() => {
    if (!hasSocial) return undefined;
    let alive = true;
    axios.get(`${API}/api/example-videos/public`)
      .then((r) => { if (alive) setVideos(applyCustomExamples(r.data, proposal)); })
      .catch(() => { if (alive) setVideos(EMPTY_VIDEOS); });
    return () => { alive = false; };
  }, [hasSocial, proposal]);

  // Detect whether any vendor pricing was set anywhere — controls visibility of the reveal toggle
  const hasAnyVendorPricing = useMemo(() => {
    const anyOnProducts = (proposal.selected_products || []).some(
      (p) => p.vendor_price !== null && p.vendor_price !== undefined && Number(p.vendor_price) > 0
    );
    const anyOnBundles = (proposal.bundles || []).some(
      (b) =>
        (b.vendor_price_without_epc !== null && b.vendor_price_without_epc !== undefined) ||
        (b.vendor_price_with_epc !== null && b.vendor_price_with_epc !== undefined)
    );
    return anyOnProducts || anyOnBundles;
  }, [proposal]);

  const renderPackages = (bundlesList) => (
    <section className="nbp-bundles-section" key="packages" data-testid="nbp-packages">
      <div className="nbp-section-head">
        <span className="nbp-eyebrow-dark">Your bespoke packages</span>
        <h2 className="nbp-h2">Pick the package that fits each property</h2>
        <p className="nbp-section-sub">
          Built for your agency — bundle pricing for the homes you list most often.
        </p>
      </div>
      <div className={`nbp-bundle-grid ${bundlesList.length === 3 ? 'nbp-bundle-grid-3' : ''}`}>
        {bundlesList.map((b, idx) => (
          <BundleCard
            key={b.id || idx}
            bundle={b}
            productById={productById}
            revealVendor={revealVendor}
            epcEnabled={epcEnabled}
          />
        ))}
        {bundlesList.length === 3 && (
          <div className="nbp-bundle-filler" data-testid="nbp-bundle-filler" aria-hidden="true">
            <img
              src="https://customer-assets.emergentagent.com/job_65a5a688-32f9-4c0e-ac9b-de9d4b5997f9/artifacts/7e73kucy_NicheCom-photographers-1600x1600.png"
              alt=""
              loading="lazy"
            />
          </div>
        )}
      </div>

      {!proposal.show_individual_pricing && smallPrint && (
        <div className="nbp-small-print" data-testid="nbp-small-print">
          <p>{smallPrint}</p>
        </div>
      )}

      {/* Add-EPC toggle below the packages — applies a £40 uplift to every card above */}
      {bundlesList.some((b) => b.includes_epc_option) && (
        <div className="nbp-epc-toggle-wrap">
          <button
            className={`nbp-epc-toggle ${epcEnabled ? "is-on" : ""}`}
            onClick={() => setEpcEnabled((v) => !v)}
            aria-pressed={epcEnabled}
            data-testid="nbp-epc-toggle"
          >
            <span className="nbp-epc-toggle-track">
              <span className="nbp-epc-toggle-knob" />
            </span>
            <span className="nbp-epc-toggle-label">
              {epcEnabled ? "EPC included" : "Add EPC"}
              <span className="nbp-epc-toggle-meta">+£40 per property</span>
            </span>
          </button>
        </div>
      )}
    </section>
  );

  // Fixed page blocks — positionable via layout_order; each keeps its own visibility rule.
  const renderFixed = (id) => {
    if (id === "pricing") {
      if (!proposal.show_individual_pricing || selectedFull.length === 0) return null;
      return (
        <ProposalPriceList
          key="pricing"
          proposal={proposal}
          orderedProducts={orderedProducts}
          orderedBundles={orderedBundles}
          smallPrint={smallPrint}
          revealVendor={revealVendor}
          onToggleReveal={() => setRevealVendor((v) => !v)}
          hasAnyVendorPricing={hasAnyVendorPricing}
        />
      );
    }
    if (id === "impact") {
      if (!roiVisible(proposal.roi, proposal.bundles)) return null;
      return <ProposalRoiCalculator key="impact" proposal={proposal} />;
    }
    if (id === "notes") {
      if (!proposal.notes) return null;
      return (
        <section className="nbp-notes-section" key="notes" data-testid="nbp-block-notes">
          <div className="nbp-section-head">
            <h2 className="nbp-h2">Notes from your account manager</h2>
          </div>
          <div className="nbp-notes">{proposal.notes}</div>
        </section>
      );
    }
    return (
      <section className="nbp-getstarted" key="getstarted" data-testid="nbp-block-getstarted">
        <div className="nbp-section-head">
          <span className="nbp-eyebrow-dark">Ready when you are</span>
          <h2 className="nbp-h2">Get started right away</h2>
          <p className="nbp-section-sub">
            Three quick ways to kick off. Pick the option that suits — we'll take it from there.
          </p>
        </div>
        <div className="nbp-getstarted-grid">
          <button
            className="nbp-gs-card nbp-gs-card-accept"
            onClick={() => setActiveCta("accept_proposal")}
            data-testid="nbp-cta-accept"
          >
            <span className="nbp-gs-icon"><Handshake size={22} /></span>
            <span className="nbp-gs-name">Accept proposal &amp; onboard</span>
            <span className="nbp-gs-body">Confirm your package, share your assets and book a kickoff call.</span>
            <span className="nbp-gs-sub">No payment required at this stage.</span>
          </button>
          <button
            className="nbp-gs-card nbp-gs-card-pay"
            onClick={() => setActiveCta("pay_card")}
            data-testid="nbp-cta-pay"
          >
            <span className="nbp-gs-icon"><CreditCard size={22} /></span>
            <span className="nbp-gs-name">Book a visit now</span>
            <span className="nbp-gs-body">Pay by card today and lock in a slot. We'll be in touch to confirm.</span>
            <span className="nbp-gs-sub">Processes order instantly.</span>
          </button>
          <button
            className="nbp-gs-card nbp-gs-card-account"
            onClick={() => setActiveCta("direct_debit")}
            data-testid="nbp-cta-dd"
          >
            <span className="nbp-gs-icon"><CalendarClock size={22} /></span>
            <span className="nbp-gs-name">Set up an account</span>
            <span className="nbp-gs-body">Settle monthly by Direct Debit — perfect if you've got volume coming.</span>
            <span className="nbp-gs-sub">Mandate sent within 24 hours.</span>
          </button>
        </div>
      </section>
    );
  };

  return (
    <div className="nbp-page" data-testid="nbp-page">
      <OverviewAudio />
      {/* ─── Hero ─── */}
      <header className="nbp-hero">
        <div className="nbp-hero-bg" aria-hidden="true" />
        <div className="nbp-hero-overlay" />
        <div className="nbp-hero-inner">
          {/* Agency logo slot parked for now — proposal.logo_url is ignored here */}
          <span className="nbp-hero-eyebrow">A bespoke proposal prepared for</span>
          <h1 className="nbp-hero-title">{proposal.agency_name}</h1>
          {proposal.agency_contact_name && (
            <p className="nbp-hero-contact">For: {proposal.agency_contact_name}</p>
          )}
          <p className="nbp-hero-blurb">
            Everything you need to win instructions and dominate locally. Captured on site,
            edited to your brand and delivered ready to publish.
          </p>
        </div>
      </header>

      {/* ─── Service sections + packages, in the RD's saved order ─── */}
      {blocks.map((block) => {
        if (block.type === "packages") return renderPackages(block.bundles);
        if (block.type === "fixed") return renderFixed(block.id);
        const sec = block;
        const i = sections.indexOf(block);
        const copy = CATEGORY_COPY[sec.category] || {
          title: sec.category,
          tagline: "",
          body: "",
        };
        const reversed = i % 2 === 1;
        const firstIcon = sec.products[0]?.product?.icon;
        const Icon = (firstIcon && ICONS[firstIcon]) || FALLBACK_ICON;
        const slug = (sec.category || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
        if (sec.category === "Social") {
          return (
            <section key={sec.category} className="nbp-svc nbp-svc-social" data-testid="nbp-social-showcase">
              <div className="nbp-svc-inner nbp-svc-inner-social">
                <div className="nbp-svc-copy">
                  <span className="nbp-svc-eyebrow">{sec.category} · Social Go &amp; Social Pro</span>
                  <h2 className="nbp-svc-title">{copy.title}</h2>
                  {copy.tagline && <p className="nbp-svc-tagline">{copy.tagline}</p>}
                  <p className="nbp-svc-body">{copy.body}</p>
                  <ul className="nbp-svc-list">
                    {sec.products.map((sp) => (
                      <li key={sp.product_id} data-testid={`nbp-svc-item-${sp.product_id}`}>
                        <CheckCircle2 size={14} /> {sp.product.name}
                        {boltOnPrice(sp) != null && <span className="nbp-svc-bolt" data-testid={`nbp-bolt-${sp.product_id}`}>{fmtPrice(boltOnPrice(sp))} as a bolt-on</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="nbp-showcase sl-proposal" data-testid="nbp-showcase">
                <ProductShowcase videos={videos || EMPTY_VIDEOS} preroll="never" />
              </div>
            </section>
          );
        }
        return (
          <section
            key={sec.category}
            className={`nbp-svc nbp-svc-${slug} ${reversed ? "nbp-svc-reverse" : ""}`}
          >
            {/* Faint architectural lines drawing themselves in the background — Floorplans only */}
            {sec.category === "Floorplans" && <FloorplanBackdrop />}
            <div className="nbp-svc-inner">
              <div className="nbp-svc-copy">
                <span className="nbp-svc-eyebrow">{sec.category}</span>
                <h2 className="nbp-svc-title">{copy.title}</h2>
                {copy.tagline && <p className="nbp-svc-tagline">{copy.tagline}</p>}
                <p className="nbp-svc-body">{copy.body}</p>
                <ul className="nbp-svc-list">
                  {sec.products.map((sp) => (
                    <li key={sp.product_id} data-testid={`nbp-svc-item-${sp.product_id}`}>
                      <CheckCircle2 size={14} /> {sp.product.name}
                      {photoLabel(sp) && <span className="nbp-svc-count" data-testid={`nbp-photos-${sp.product_id}`}>{photoLabel(sp)}</span>}
                      {boltOnPrice(sp) != null && <span className="nbp-svc-bolt" data-testid={`nbp-bolt-${sp.product_id}`}>{fmtPrice(boltOnPrice(sp))} as a bolt-on</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="nbp-svc-visual">
                {sec.category === "EPCs" ? (
                  <EPCChart />
                ) : CATEGORY_VIDEO[sec.category] ? (
                  <div className="nbp-video-stage" data-testid="nbp-video">
                    <div className="nbp-video-frame">
                      <video
                        src={CATEGORY_VIDEO[sec.category].landscape}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="nbp-video-landscape"
                      />
                    </div>
                    {/* iPhone PIP — shows the portrait variant of the property tour */}
                    <div className="nbp-video-phone" aria-hidden="true">
                      <div className="nbp-video-phone-notch" />
                      <video
                        src={CATEGORY_VIDEO[sec.category].portrait}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="nbp-video-portrait"
                      />
                    </div>
                  </div>
                ) : CATEGORY_MEDIA[sec.category] ? (
                  <FadeSlideshow
                    images={CATEGORY_MEDIA[sec.category]}
                    alt={`${copy.title} examples`}
                  />
                ) : (
                  <div className="nbp-svc-icon-wrap">
                    <Icon size={48} />
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      {activeCta && (
        <OnboardingModal
          cta={activeCta}
          proposal={proposal}
          epcEnabled={epcEnabled}
          onClose={() => setActiveCta(null)}
        />
      )}

      <footer className="nbp-footer">
        <p>Prepared by NicheCom · This proposal is confidential and intended for {proposal.agency_name}.</p>
      </footer>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Single bundle card
   ───────────────────────────────────────────────────────────── */
function BundleCard({ bundle, productById, revealVendor, epcEnabled = false }) {
  const products = bundle.product_ids.map((id) => productById[id]).filter(Boolean);
  const hasEpcOption = bundle.includes_epc_option;
  const baseVendor = bundle.vendor_price_without_epc;
  const epcVendor = bundle.vendor_price_with_epc;
  const baseMargin = baseVendor > bundle.price_without_epc ? baseVendor - bundle.price_without_epc : 0;
  const epcMargin = epcVendor && bundle.price_with_epc && epcVendor > bundle.price_with_epc
    ? epcVendor - bundle.price_with_epc
    : 0;

  // Effective price to display when the global "Add EPC" toggle is ON
  const showEpcUpsold = hasEpcOption && epcEnabled;
  const displayPrice = showEpcUpsold ? bundle.price_with_epc : bundle.price_without_epc;
  const displayResale = showEpcUpsold ? epcVendor : baseVendor;

  return (
    <div className="nbp-bundle-card" data-testid={`nbp-bundle-${bundle.name}`}>
      <div className="nbp-bundle-name">{bundle.name || "Untitled bundle"}</div>

      {/* Pricing — display style depends on epc_display */}
      {!hasEpcOption ? (
        <div className="nbp-bundle-price-row">
          <div className="nbp-bundle-price">
            <span className="nbp-bundle-price-label">Per property</span>
            <span className="nbp-bundle-price-num">{fmtPrice(bundle.price_without_epc)}</span>
            {revealVendor && baseVendor && (
              <>
                <span className="nbp-bundle-resale">Resale: {fmtPrice(baseVendor)}</span>
                {baseMargin > 0 && (
                  <span className="nbp-bundle-margin">
                    <TrendingUp size={11} /> +{fmtPrice(baseMargin)} margin
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      ) : bundle.epc_display === "inline" ? (
        <div className="nbp-bundle-price-row">
          <div className="nbp-bundle-price">
            <span className="nbp-bundle-price-label">{showEpcUpsold ? "Per property" : "From"}</span>
            <span className="nbp-bundle-price-num">{fmtPrice(displayPrice)}</span>
            {!showEpcUpsold ? (
              <span className="nbp-bundle-inline-epc">
                +{fmtPrice((bundle.price_with_epc || 0) - bundle.price_without_epc)} if EPC needed
              </span>
            ) : (
              <span className="nbp-bundle-inline-epc nbp-bundle-inline-epc-on">
                Includes EPC (+£40)
              </span>
            )}
            {revealVendor && displayResale && (
              <span className="nbp-bundle-resale">Resale: {fmtPrice(displayResale)}</span>
            )}
          </div>
        </div>
      ) : (
        <div className="nbp-bundle-prices">
          <div className={`nbp-bundle-price ${!showEpcUpsold ? "nbp-bundle-price-active" : ""}`}>
            <span className="nbp-bundle-price-label">Without EPC</span>
            <span className="nbp-bundle-price-num">{fmtPrice(bundle.price_without_epc)}</span>
            {revealVendor && baseVendor && (
              <>
                <span className="nbp-bundle-resale">Resale: {fmtPrice(baseVendor)}</span>
                {baseMargin > 0 && (
                  <span className="nbp-bundle-margin">
                    <TrendingUp size={11} /> +{fmtPrice(baseMargin)}
                  </span>
                )}
              </>
            )}
          </div>
          <div className={`nbp-bundle-price nbp-bundle-price-epc ${showEpcUpsold ? "nbp-bundle-price-active" : ""}`}>
            <span className="nbp-bundle-price-label">With EPC</span>
            <span className="nbp-bundle-price-num">{fmtPrice(bundle.price_with_epc)}</span>
            {revealVendor && epcVendor && (
              <>
                <span className="nbp-bundle-resale">Resale: {fmtPrice(epcVendor)}</span>
                {epcMargin > 0 && (
                  <span className="nbp-bundle-margin">
                    <TrendingUp size={11} /> +{fmtPrice(epcMargin)}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className="nbp-bundle-includes">
        <span className="nbp-bundle-includes-label">Includes</span>
        <ul>
          {products.map((p) => (
            <li key={p.id}>
              <CheckCircle2 size={12} /> {p.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
   Onboarding / payment modal — shared by all three CTAs
   ───────────────────────────────────────────────────────────── */
function OnboardingModal({ cta, proposal, epcEnabled, onClose }) {
  const bundles = proposal.bundles || [];
  // Pre-select the cheapest bundle by default so the form starts populated
  const defaultBundle = bundles.length > 0
    ? bundles.slice().sort((a, b) => (a.price_without_epc || 0) - (b.price_without_epc || 0))[0]
    : null;

  const [selectedBundleName, setSelectedBundleName] = useState(defaultBundle?.name || "");
  const [includeEpc, setIncludeEpc] = useState(epcEnabled);
  const [contactName, setContactName] = useState(proposal.agency_contact_name || "");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [companyName, setCompanyName] = useState(proposal.agency_name || "");
  const [billingAddress, setBillingAddress] = useState("");
  const [preferredCallAt, setPreferredCallAt] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedBundle = bundles.find((b) => b.name === selectedBundleName);
  const basePrice = selectedBundle?.price_without_epc || 0;
  const totalPrice = basePrice + (includeEpc && selectedBundle?.includes_epc_option ? 40 : 0);

  const heading = {
    accept_proposal: "Accept proposal & onboard",
    pay_card: "Book a visit — pay by card",
    direct_debit: "Set up your account",
  }[cta];

  const subline = {
    accept_proposal: "Tell us a little about you and we'll be in touch to schedule a kickoff call.",
    pay_card: "Pay securely via Stripe. We'll confirm your visit and onboard you within 1 business day.",
    direct_debit: "We'll send a Direct Debit mandate via email within 24 hours. No payment today.",
  }[cta];

  async function handleSubmit(e) {
    e.preventDefault();
    if (!contactName || !contactEmail) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    if (cta === "pay_card" && !selectedBundleName) {
      toast.error("Please pick a package before paying.");
      return;
    }

    setSubmitting(true);
    try {
      const orderRes = await axios.post(`${API}/api/proposals/${proposal.slug}/orders`, {
        cta,
        bundle_name: selectedBundleName || null,
        bundle_price: selectedBundle?.price_without_epc || null,
        include_epc: !!(includeEpc && selectedBundle?.includes_epc_option),
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone || null,
        company_name: companyName || null,
        billing_address: billingAddress || null,
        preferred_call_at: preferredCallAt || null,
        notes: notes || null,
      });
      const orderId = orderRes.data.order_id;

      if (cta === "pay_card") {
        // Create Stripe Checkout session and redirect
        const sessionRes = await axios.post(`${API}/api/proposals/checkout/session`, {
          order_id: orderId,
          origin_url: window.location.origin,
        });
        if (sessionRes.data?.url) {
          window.location.href = sessionRes.data.url;
          return;
        }
        toast.error("Could not start payment — please try again.");
        setSubmitting(false);
        return;
      }

      toast.success(
        cta === "accept_proposal"
          ? "Welcome aboard! We'll be in touch shortly."
          : "Thanks — we'll send your Direct Debit mandate within 24 hours."
      );
      onClose();
    } catch (err) {
      console.error("Order submission failed", err);
      toast.error(err.response?.data?.detail || "Something went wrong — please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="nbp-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="nbp-modal" onClick={(e) => e.stopPropagation()} data-testid="nbp-modal">
        <button className="nbp-modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <div className="nbp-modal-head">
          <h3 className="nbp-modal-title">{heading}</h3>
          <p className="nbp-modal-sub">{subline}</p>
        </div>
        <form onSubmit={handleSubmit} className="nbp-modal-body">
          {bundles.length > 0 && (
            <div className="nbp-modal-field">
              <label className="nbp-modal-label">Package</label>
              <div className="nbp-modal-bundles" role="radiogroup">
                {bundles.map((b) => {
                  const checked = b.name === selectedBundleName;
                  return (
                    <button
                      type="button"
                      key={b.name || b.id}
                      role="radio"
                      aria-checked={checked}
                      className={`nbp-modal-bundle ${checked ? "is-on" : ""}`}
                      onClick={() => setSelectedBundleName(b.name)}
                      data-testid={`nbp-modal-bundle-${b.name}`}
                    >
                      <span className="nbp-modal-bundle-name">{b.name}</span>
                      <span className="nbp-modal-bundle-price">{fmtPrice(b.price_without_epc)}</span>
                    </button>
                  );
                })}
              </div>
              {selectedBundle?.includes_epc_option && (
                <label className="nbp-modal-epc">
                  <input
                    type="checkbox"
                    checked={includeEpc}
                    onChange={(e) => setIncludeEpc(e.target.checked)}
                    data-testid="nbp-modal-epc-toggle"
                  />
                  <span>Include EPC (+£40)</span>
                </label>
              )}
            </div>
          )}

          <div className="nbp-modal-row">
            <div className="nbp-modal-field">
              <label className="nbp-modal-label">Your name *</label>
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
                data-testid="nbp-modal-name"
              />
            </div>
            <div className="nbp-modal-field">
              <label className="nbp-modal-label">Email *</label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
                data-testid="nbp-modal-email"
              />
            </div>
          </div>

          <div className="nbp-modal-row">
            <div className="nbp-modal-field">
              <label className="nbp-modal-label">Phone</label>
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>
            <div className="nbp-modal-field">
              <label className="nbp-modal-label">Agency / Company</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
          </div>

          {(cta === "pay_card" || cta === "direct_debit") && (
            <div className="nbp-modal-field">
              <label className="nbp-modal-label">Billing address</label>
              <textarea
                rows={2}
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
              />
            </div>
          )}

          {cta !== "pay_card" && (
            <div className="nbp-modal-field">
              <label className="nbp-modal-label">When's a good time to call you?</label>
              <input
                type="datetime-local"
                value={preferredCallAt}
                onChange={(e) => setPreferredCallAt(e.target.value)}
              />
            </div>
          )}

          <div className="nbp-modal-field">
            <label className="nbp-modal-label">Anything else we should know?</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Logo links, special requests, lead times…"
            />
          </div>

          {cta === "pay_card" && selectedBundle && (
            <div className="nbp-modal-total">
              <span>Total today</span>
              <strong>{fmtPrice(totalPrice)}</strong>
            </div>
          )}

          <div className="nbp-modal-actions">
            <button type="button" className="nbp-modal-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="nbp-modal-submit"
              disabled={submitting}
              data-testid="nbp-modal-submit"
            >
              {submitting && <Loader2 size={14} className="nbp-spin" />}
              {cta === "pay_card" ? `Pay ${fmtPrice(totalPrice)}` : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
